import { useEffect, useRef, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { Flex, Content, View, Link, ProgressCircle, DialogContainer, Dialog, TextArea, Header } from '@adobe/react-spectrum';
import ExternalContactsSearch from './component/ExternalContactsSearch';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import ContactCard from './component/ContactCard';
import useComonentReload from '../../../../hooks/UseComponentReload';
import ExternalNewContact from './component/ExternalNewContact';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { UpdateActivityRequest } from '../../../../services/soap/project/requests/UpdateActivityRequest';
import { GetProjectRequest } from '../../../../services/soap/project/requests/GetProjectRequest';
import { UpdateProjectRequest } from '../../../../services/soap/project/requests/UpdateProjectRequest';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { UpdateFolderRequest } from '../../../../services/soap/project/requests/UpdateFolderRequest';
import styles from './ExternalContacts.module.css';

interface ExternalContactsProps {
  selectedItem?: FPAData;
}

type UpdateRequestFunction = () => Promise<{ result: string }>;

function ExternalContacts({ selectedItem }: ExternalContactsProps) {
  const [reloadComponent] = useComonentReload();
  const { projectService, store } = useDependency();
  const [externalContacts, setExternalContacts] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [isOpenSearchContact, setIsOpenSearchContact] = useState<boolean>(false);
  const [isOpenNewContact, setIsOpenNewContact] = useState<boolean>(false);
  const { t } = useTranslation();

  const getExternalContactsArray = (item: any) => {
    if (!item?.EXTERNAL_CONTACTS) return [];

    if (selectedItem?.type === FPADataTypes.ACTIVITY) {
      const rowsSource = item.EXTERNAL_CONTACTS?.ROWS;
      const rows = Array.isArray(rowsSource)
        ? rowsSource
        : rowsSource?.EXTERNAL_CONTACT
          ? [{ EXTERNAL_CONTACT: rowsSource.EXTERNAL_CONTACT }]
          : [];
      return rows
        .map((row: any) => row?.EXTERNAL_CONTACT)
        .flat()
        .filter(Boolean);
    }

    if (Array.isArray(item.EXTERNAL_CONTACTS?.EXTERNAL_CONTACT)) {
      return item.EXTERNAL_CONTACTS.EXTERNAL_CONTACT;
    }

    const rowsSource = item.EXTERNAL_CONTACTS?.ROWS;
    const contactsFromRows = Array.isArray(rowsSource)
      ? rowsSource.map((row: any) => row?.EXTERNAL_CONTACT)
      : rowsSource?.EXTERNAL_CONTACT
        ? [rowsSource.EXTERNAL_CONTACT]
        : [];

    return contactsFromRows.flat().filter(Boolean);
  };

  const normalizeExternalContactsForSave = (item: any) => {
    if (!item?.EXTERNAL_CONTACTS || !selectedItem) return item;

    const contacts = getExternalContactsArray(item);

    if (selectedItem.type === FPADataTypes.ACTIVITY) {
      return {
        ...item,
        EXTERNAL_CONTACTS: {
          count: String(contacts.length),
          ROWS: contacts.map((contact: any) => ({
            EXTERNAL_CONTACT: contact,
          })),
        },
      };
    }

    return {
      ...item,
      EXTERNAL_CONTACTS: {
        count: String(contacts.length),
        EXTERNAL_CONTACT: contacts,
      },
    };
  };

  const forEachMutableExternalContact = (callback: (contact: any) => void) => {
    if (!current_Item.current?.EXTERNAL_CONTACTS) return;

    if (selectedItem?.type === FPADataTypes.ACTIVITY) {
      const rowsSource = current_Item.current.EXTERNAL_CONTACTS?.ROWS;
      const rows = Array.isArray(rowsSource)
        ? rowsSource
        : rowsSource?.EXTERNAL_CONTACT
          ? [{ EXTERNAL_CONTACT: rowsSource.EXTERNAL_CONTACT }]
          : [];
      rows.forEach((row: any) => {
        const contacts = Array.isArray(row?.EXTERNAL_CONTACT) ? row.EXTERNAL_CONTACT : [row?.EXTERNAL_CONTACT];
        contacts.filter(Boolean).forEach((contact: any) => callback(contact));
      });
      return;
    }

    const contacts = Array.isArray(current_Item.current.EXTERNAL_CONTACTS?.EXTERNAL_CONTACT)
      ? current_Item.current.EXTERNAL_CONTACTS.EXTERNAL_CONTACT
      : current_Item.current.EXTERNAL_CONTACTS?.ROWS?.EXTERNAL_CONTACT
        ? [current_Item.current.EXTERNAL_CONTACTS.ROWS.EXTERNAL_CONTACT].flat()
        : [];
    contacts.forEach((contact: any) => callback(contact));
  };

  const current_Item = useRef<any>(null);
  useEffect(() => {
    (async () => {
      await loadExternalContacts();
    })();
  }, [selectedItem, reloadComponent]);

  const loadExternalContacts = async () => {
    if (!selectedItem) return;
    setShowLoader(true);
    const setCurrentItemAndContacts = (item: any) => {
      const normalizedItem = normalizeExternalContactsForSave(item);
      current_Item.current = normalizedItem;
      setExternalContacts(getExternalContactsArray(normalizedItem));
    };

    let item;
    switch (selectedItem.type) {
      case FPADataTypes.ACTIVITY:
        item = await projectService.getActivity(new GetActivityRequest(store.Server, store.SessionId, Number(selectedItem.id)));
        setCurrentItemAndContacts(item.ACTIVITY);
        break;
      case FPADataTypes.PROJECT:
        item = await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, selectedItem.id));
        setCurrentItemAndContacts(item.PROJECT);
        break;
      case FPADataTypes.FOLDER:
        item = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, selectedItem.id));
        setCurrentItemAndContacts(item.FOLDER);
        break;
    }
    setShowLoader(false);
  };

  const updateItem = async (updateRequest: UpdateRequestFunction, successMessage: string) => {
    const updatedItem = await updateRequest();
    if (updatedItem.result === 'OK') {
      await loadExternalContacts();
      ToastQueue.positive(successMessage, { timeout: 3000 });
    }
  };

  const updateContactNote = (contactId: string, description: string) => {
    forEachMutableExternalContact((contact: any) => {
      const currentContactId = String(contact?.id || contact?.contactId || '');
      if (currentContactId === contactId) {
        contact.note = description;
        contact.deleteContact = '0';
      } else {
        contact.deleteContact = '0';
      }
    });
  };

  const onSaveDescription = async (contactId: string, description: string) => {
    if (!selectedItem) return;

    updateContactNote(contactId, description);

    const requestData = {
      server: store.Server,
      sessionId: store.SessionId,
      data: {
        ...current_Item.current,
        EXTERNAL_CONTACTS: current_Item.current.EXTERNAL_CONTACTS,
      },
    };

    let successMessage = t('description_saved_successfully', { ns: 'contacts' });

    switch (selectedItem.type) {
      case FPADataTypes.ACTIVITY:
        await updateItem(() => projectService.updateActivity(new UpdateActivityRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
      case FPADataTypes.PROJECT:
        await updateItem(() => projectService.updateProject(new UpdateProjectRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
      case FPADataTypes.FOLDER:
        await updateItem(() => projectService.updateFolder(new UpdateFolderRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
    }
  };

  const updateContactRating = (contactId: string, rating: string) => {
    forEachMutableExternalContact((contact: any) => {
      const currentContactId = String(contact?.id || contact?.contactId || '');
      if (currentContactId === contactId) {
        contact.relationshipLevel = rating;
        contact.deleteContact = '0';
      } else {
        contact.deleteContact = '0';
      }
    });
  };

  const onSaveRating = async (contactId: string, rating: string) => {
    if (!selectedItem) return;

    updateContactRating(contactId, rating);

    const requestData = {
      server: store.Server,
      sessionId: store.SessionId,
      data: {
        ...current_Item.current,
        EXTERNAL_CONTACTS: current_Item.current.EXTERNAL_CONTACTS,
      },
    };
    let successMessage = t('rating_saved_successfully', { ns: 'layout_components' });

    switch (selectedItem.type) {
      case FPADataTypes.ACTIVITY:
        await updateItem(() => projectService.updateActivity(new UpdateActivityRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
      case FPADataTypes.PROJECT:
        await updateItem(() => projectService.updateProject(new UpdateProjectRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
      case FPADataTypes.FOLDER:
        await updateItem(() => projectService.updateFolder(new UpdateFolderRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
    }
  };

  const openSearchDialog = () => {
    setIsOpenSearchContact(true);
    setIsOpenNewContact(false);
  };

  const closeSearchDialog = async (reload: boolean) => {
    //console.log('reload', reload);
    reload && (await loadExternalContacts());
    setIsOpenSearchContact(false);
  };

  const openNewContactDialog = () => {
    setIsOpenSearchContact(false);
    setIsOpenNewContact(true);
  };

  const closeNewContactDialog = async (reload: boolean) => {
    reload && (await loadExternalContacts());
    setIsOpenNewContact(false);
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} gap={'size-150'} position={'relative'} width={'100%'}>
        <Flex direction={'row'} alignItems={'start'} justifyContent={'start'}>
          <Content position={'relative'} UNSAFE_className={styles.heading_text}>
            {t('client_contact', { ns: 'layout_components' })}
          </Content>
          <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'size-100'} UNSAFE_style={{paddingRight:'10px'}} UNSAFE_className={styles.icon_add_parent}>
            <Content>
              <Link
                isQuiet
                onPress={e => {
                  setIsOpenSearchContact(true);
                }}
              >
                <i className="bi bi-plus fs-5">
                  <View UNSAFE_className={styles.icon_add_text}>{t('add', { ns: 'layout_components' })}</View>
                </i>
              </Link>
            </Content>
          </Flex>
        </Flex>
        <Flex direction={'column'}>
          <Flex maxHeight={{ base: '1000px', L: '450px', M: '450px' }} width="100%" direction="column" UNSAFE_style={{ overflowX: 'auto' }}>
            {externalContacts?.map((contact: any, index: number) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                avatarSrc="../../../../../../assets/images/avatar--desktop--light@2x.png"
                selectedItem={selectedItem}
                FPADataTypes={FPADataTypes}
                onSaveDescription={onSaveDescription}
                onSaveRating={onSaveRating}
                currentItem={current_Item}
                updateItem={updateItem}
                closeDialog={closeNewContactDialog}
              />
            ))}
          </Flex>

          <DialogContainer
            isDismissable
            onDismiss={() => {
              setIsOpenSearchContact(false);
            }}
          >
            {isOpenSearchContact && (
              <Dialog>
                <Header UNSAFE_className={styles.add_contact_heading}>{t('add_client_contact', { ns: 'layout_components' })}</Header>
                <Content>
                  <ExternalContactsSearch closeDialog={closeSearchDialog} openDialog={openNewContactDialog} currentItem={current_Item} selectedItem={selectedItem} />
                </Content>
              </Dialog>
            )}
          </DialogContainer>

          <DialogContainer
            isDismissable
            onDismiss={() => {
              setIsOpenNewContact(false);
            }}
          >
            {isOpenNewContact && (
              <ExternalNewContact
                selectedItem={selectedItem}
                currentItem={current_Item}
                FPADataTypes={FPADataTypes}
                updateItem={updateItem}
                closeDialog={closeNewContactDialog}
                openDialog={openSearchDialog}
              />
            )}
          </DialogContainer>
        </Flex>
      </Flex>
    );
  }
}

export const ExternalContactsComponent = LayoutComponent(ExternalContacts);
